import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';

interface ToggleChartControlProps {
  setChartType?: (value: string) => void;
  numAssessmentPeriods?: number;
  chartType?: 'bubble' | 'bar' | 'line';
}

const ToggleChartControl: React.FC<ToggleChartControlProps> = ({
  numAssessmentPeriods = 0,
  setChartType = () => {},
  chartType = 'bar',
}) => {
  const [activeChart, setActiveChart] = useState<'bubble' | 'bar' | 'line'>(chartType);

  // Dynamically compute if buttons should be active based on `numAssessmentPeriods`
  const isBarActive = numAssessmentPeriods >= 1;
  const isBubbleActive = numAssessmentPeriods >= 2;
  const isLineActive = numAssessmentPeriods >= 2;

  const handleToggle = (chartType: 'bubble' | 'bar' | 'line') => {
    if (
      (chartType === 'bar' && isBarActive) ||
      (chartType === 'bubble' && isBubbleActive) ||
      (chartType === 'line' && isLineActive)
    ) {
      setActiveChart(chartType);
      setChartType(chartType);
    }
  };

  useEffect(() => {
    setActiveChart(chartType);
  }, [chartType]);

  // Return nothing if no assessment periods
  if (numAssessmentPeriods === 0) {
    return null;
  }

  return (
    <ToggleContainer>
      <ToggleButton
        isActive={activeChart === 'bar'}
        isDisabled={!isBarActive}
        onClick={() => handleToggle('bar')}
      >
        <Icon name="chart bar" style={{ fontSize: 18, lineHeight: 1 }} />
      </ToggleButton>
      <ToggleButton
        isActive={activeChart === 'bubble'}
        isDisabled={!isBubbleActive}
        onClick={() => handleToggle('bubble')}
      >
        <Icon name="braille" style={{ fontSize: 18, lineHeight: 1 }} />
      </ToggleButton>
      <ToggleButton
        isActive={activeChart === 'line'}
        isDisabled={!isLineActive}
        onClick={() => handleToggle('line')}
      >
        <Icon name="chart line" style={{ fontSize: 18, lineHeight: 1 }} />
      </ToggleButton>
    </ToggleContainer>
  );
};

export default ToggleChartControl;

// Styled Components
const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 0;
  align-items: center;
`;

const ToggleButton = styled.button<{ isActive: boolean; isDisabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${({ isActive }) => (isActive ? '#f0f0f0' : '#ffffff')};
  color: ${({ isActive, isDisabled }) =>
    isDisabled ? '#cccccc' : isActive ? '#333333' : '#777777'};
  border: 1px solid
    ${({ isActive, isDisabled }) => (isDisabled ? '#dddddd' : isActive ? '#aaaaaa' : '#dddddd')};
  padding: 0;
  font-size: 0;
  cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
  transition: all 0.3s ease;
  border-radius: 6px;

  &:hover {
    background-color: ${({ isActive, isDisabled }) =>
      isDisabled ? '#ffffff' : isActive ? '#d5d5d5' : '#f7f7f7'};
    color: ${({ isDisabled }) => (isDisabled ? '#cccccc' : '#555555')};
    border: 1px solid ${({ isDisabled }) => (isDisabled ? '#dddddd' : '#aaaaaa')};
  }
`;
